import { useCallback } from 'react'
import { MobileApp } from '@mobileTypes'
import {
  useCopyMobileAppMutation,
  useCreateMobileAppMutation,
  useDeleteMobileAppMutation,
  useFetchAllMobileAppsQuery,
  useUpdateMobileAppMutation,
} from '../services'

export const useMobileApps = () => {
  const { data: apps = [], isLoading: loading } = useFetchAllMobileAppsQuery()
  const [createMobileApp] = useCreateMobileAppMutation()
  const [updateMobileApp] = useUpdateMobileAppMutation()
  const [deleteMobileApp] = useDeleteMobileAppMutation()
  const [copyMobileApp] = useCopyMobileAppMutation()

  const createApp = useCallback(
    (appData: Omit<MobileApp, 'id' | 'createdAt' | 'updatedAt'>): Promise<MobileApp> =>
      createMobileApp(appData).unwrap(),
    [createMobileApp]
  )

  const updateApp = useCallback(
    (id: string, appData: Partial<MobileApp>): Promise<MobileApp> => {
      const numericId = Number(id)
      const existing = apps.find(app => app.id === numericId)
      const merged: MobileApp = {
        ...(existing ?? ({} as MobileApp)),
        ...appData,
        id: numericId,
      }

      return updateMobileApp(merged).unwrap()
    },
    [apps, updateMobileApp]
  )

  const deleteApp = useCallback(
    async (id: string | number): Promise<void> => {
      await deleteMobileApp(String(id)).unwrap()
    },
    [deleteMobileApp]
  )

  const getAppById = useCallback(
    (id: string): MobileApp | undefined => apps.find(app => app.id === Number(id)),
    [apps]
  )

  const copyApp = useCallback(
    async (id: string | number, newName: string, newInternalId: string): Promise<MobileApp | null> => {
      try {
        return await copyMobileApp({ id: String(id), name: newName, internalId: newInternalId }).unwrap()
      } catch {
        return null
      }
    },
    [copyMobileApp]
  )

  return {
    apps,
    loading,
    createApp,
    updateApp,
    deleteApp,
    getAppById,
    copyApp,
  }
}
